/**
 * Widget V2 - EmptyState Component
 *
 * Display when no data is available.
 */

import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import InboxIcon from "@mui/icons-material/Inbox";

/**
 * EmptyState component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Custom icon (default: InboxIcon)
 * @param {string} props.title - Main message
 * @param {string} props.description - Secondary message
 * @param {string} props.actionLabel - Action button label
 * @param {function} props.onAction - Action button handler
 */
function EmptyState({ icon, title = "No results found", description, actionLabel, onAction }) {
  const IconComponent = icon || InboxIcon;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 4, sm: 6 },
        px: 3,
        textAlign: "center"
      }}
    >
      <Box
        sx={{
          width: { xs: "4rem", sm: "5rem" },
          height: { xs: "4rem", sm: "5rem" },
          borderRadius: "50%",
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: { xs: 1.5, sm: 2 }
        }}
      >
        {React.isValidElement(icon) ? icon : <IconComponent sx={{ fontSize: { xs: "2rem", sm: "2.5rem" }, color: "text.secondary" }} />}
      </Box>

      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      {description && (
        <Typography variant="body2" color="textSecondary" sx={{ maxWidth: "18.75rem", mb: actionLabel ? 3 : 0 }}>
          {description}
        </Typography>
      )}

      {actionLabel && onAction && (
        <Button variant="contained" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

export default EmptyState;
